import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { Briefcase, RefreshCw, Loader2, Search, ExternalLink, Handshake, Flame, UserPlus, Check } from "lucide-react";

const CATEGORY_STYLE = {
  cession: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  faillite: "bg-red-500/15 text-red-400 border-red-500/30",
};

const CATEGORY_LABEL = { cession: "Cession / succession", faillite: "Faillite (distressed)" };

const HEAT_STYLE = {
  hot: "bg-orange-500/20 text-orange-300 border-orange-500/40",
  warm: "bg-[#378ADD]/15 text-[#378ADD] border-[#378ADD]/30",
};
const HEAT_LABEL = { hot: "Cible chaude", warm: "Angle d'entrée" };

export function SuccessionView() {
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [hotCount, setHotCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState({ category: "", canton: "", q: "" });
  const [converted, setConverted] = useState({});
  const [converting, setConverting] = useState(null);

  const convertLead = async (deal) => {
    setConverting(deal.deal_id);
    try {
      const r = await api.post("/workspace/deals/succession/to-lead", { deal_id: deal.deal_id });
      setConverted((c) => ({ ...c, [deal.deal_id]: true }));
      notify.success(r.data?.already_exists ? "Déjà dans vos Leads." : "Converti en Lead ✓");
    } catch (err) {
      notify.error(fmtError(err, "Conversion impossible."));
    } finally { setConverting(null); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 150 };
      if (filter.category) params.category = filter.category;
      if (filter.canton) params.canton = filter.canton;
      if (filter.q) params.q = filter.q;
      const [s, e] = await Promise.all([
        api.get("/workspace/deals/succession/summary"),
        api.get("/workspace/deals/succession", { params }),
      ]);
      setSummary(s.data);
      setItems(e.data?.items || []);
      setHotCount(e.data?.hot_count || 0);
    } catch (err) {
      notify.error(fmtError(err, "Chargement deal flow impossible."));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.category, filter.canton]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      await api.post("/workspace/deals/succession/refresh");
      notify.success("Scan lancé — rechargez dans ~1 minute.");
      setTimeout(load, 4000);
    } catch (err) {
      notify.error(fmtError(err, "Scan impossible."));
    } finally { setRefreshing(false); }
  };

  const byCat = summary?.by_category || {};

  return (
    <div className="space-y-4" data-testid="succession-view">
      <div className="noam-card p-4 flex items-start gap-2 border-[#378ADD]/25 bg-[#378ADD]/5">
        <Handshake className="w-4 h-4 text-[#378ADD] flex-shrink-0 mt-0.5" />
        <div className="text-xs text-zinc-300 leading-relaxed">
          <strong className="text-[#378ADD]">Veille succession PME.</strong> Cessions volontaires
          (relevepme.ch) et faillites d'entreprises (FOSC / SHAB officiel). Croisement automatique
          avec votre Annuaire : <strong className="text-zinc-100">{summary?.annuaire_matches ?? 0}</strong> correspondance(s) détectée(s).
          {hotCount > 0 && (
            <span className="ml-1">Dont <strong className="text-orange-300">{hotCount} cible(s) chaude(s)</strong> où vous avez déjà un angle d'entrée (remontées en tête).</span>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => setFilter((f) => ({ ...f, category: "" }))}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${!filter.category ? "bg-[#378ADD]/15 text-[#378ADD] border-[#378ADD]/40" : "text-zinc-400 border-white/10 hover:text-zinc-200"}`}
            data-testid="succession-cat-all"
          >
            Tous ({summary?.total ?? 0})
          </button>
          {["cession", "faillite"].map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setFilter((f) => ({ ...f, category: f.category === k ? "" : k }))}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${filter.category === k ? "bg-[#378ADD]/15 text-[#378ADD] border-[#378ADD]/40" : "text-zinc-400 border-white/10 hover:text-zinc-200"}`}
              data-testid={`succession-cat-${k}`}
            >
              {CATEGORY_LABEL[k]} ({byCat[k] ?? 0})
            </button>
          ))}
        </div>
        <button type="button" onClick={refresh} disabled={refreshing} className="btn-primary text-xs disabled:opacity-50" data-testid="succession-refresh-btn">
          {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          {refreshing ? "Scan…" : "Scanner maintenant"}
        </button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            value={filter.q}
            onChange={(e) => setFilter((f) => ({ ...f, q: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Rechercher (titre, secteur)…"
            className="noam-input pl-8 text-xs h-8 w-60"
            data-testid="succession-search-input"
          />
        </div>
        <select
          value={filter.canton}
          onChange={(e) => setFilter((f) => ({ ...f, canton: e.target.value }))}
          className="noam-input text-xs h-8 w-44"
          data-testid="succession-canton-select"
        >
          <option value="">Tous les cantons</option>
          {Object.entries(summary?.by_canton || {}).map(([c, n]) => (
            <option key={c} value={c}>{c} ({n})</option>
          ))}
        </select>
        {summary?.last_crawl_at && (
          <span className="text-[10px] text-zinc-500 ml-auto" data-testid="succession-last-crawl">
            Dernier scan : {new Date(summary.last_crawl_at).toLocaleString("fr-CH")}
          </span>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-zinc-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Chargement…
        </div>
      ) : items.length === 0 ? (
        <div className="noam-card p-8 text-center text-sm text-zinc-500" data-testid="succession-empty">
          <Briefcase className="w-8 h-8 mx-auto mb-2 opacity-40" />
          Aucune annonce. Lancez un scan pour alimenter le deal flow.
        </div>
      ) : (
        <div className="space-y-2" data-testid="succession-list">
          {items.map((it) => (
            <div key={it.deal_id} className="noam-card p-4 hover:border-white/20 transition-colors" data-testid="succession-item">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    {it.heat_level && it.heat_level !== "cold" && (
                      <span className={`px-2 py-0.5 rounded text-[10px] font-semibold border inline-flex items-center gap-1 ${HEAT_STYLE[it.heat_level]}`} data-testid="succession-heat-badge">
                        <Flame className="w-2.5 h-2.5" /> {HEAT_LABEL[it.heat_level]}
                      </span>
                    )}
                    <span className={`px-2 py-0.5 rounded text-[10px] font-semibold border inline-flex items-center gap-1 ${CATEGORY_STYLE[it.category] || CATEGORY_STYLE.cession}`}>
                      {it.category === "faillite" && <Flame className="w-2.5 h-2.5" />}
                      {CATEGORY_LABEL[it.category] || it.category}
                    </span>
                    {it.canton && <span className="px-1.5 py-0.5 rounded text-[10px] font-mono text-zinc-400 bg-white/5">{it.canton}</span>}
                    <span className="px-1.5 py-0.5 rounded text-[10px] text-zinc-500 bg-white/5">{it.source_label}</span>
                  </div>
                  <div className="text-sm font-semibold text-zinc-100 leading-snug">{it.title}</div>
                  {it.heat_reasons && it.heat_reasons.length > 0 && (
                    <div className="text-[10px] text-orange-300/90 mt-1" data-testid="succession-heat-reasons">
                      {it.heat_reasons.join(" · ")}
                    </div>
                  )}
                  <div className="flex items-center gap-3 flex-wrap mt-1.5 text-[11px] text-zinc-400">
                    {it.sector && <span>Secteur : <span className="text-zinc-200">{it.sector}</span></span>}
                    {it.revenue_text && <span>CA : <span className="text-zinc-200">{it.revenue_text}</span></span>}
                    {it.region && !it.canton && <span>{it.region}</span>}
                  </div>
                  {it.description && <p className="text-[11px] text-zinc-500 mt-1 leading-relaxed line-clamp-2">{it.description}</p>}
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <span className="text-[10px] text-zinc-500 whitespace-nowrap">
                    {it.published_date ? new Date(it.published_date).toLocaleDateString("fr-CH") : ""}
                  </span>
                  {it.url && (
                    <a href={it.url} target="_blank" rel="noreferrer" className="text-[#EF9F27] hover:underline text-[11px] inline-flex items-center gap-1" data-testid="succession-item-link">
                      Voir <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={() => convertLead(it)}
                    disabled={converting === it.deal_id || converted[it.deal_id]}
                    className={`mt-1 text-[10px] font-semibold px-2 py-1 rounded border inline-flex items-center gap-1 transition-colors disabled:opacity-60 ${
                      converted[it.deal_id]
                        ? "border-emerald-500/40 text-emerald-400 bg-emerald-500/10"
                        : "border-[#378ADD]/40 text-[#378ADD] hover:bg-[#378ADD]/10"
                    }`}
                    data-testid="succession-convert-lead-btn"
                    title="Convertir cette cible en Lead (origination)"
                  >
                    {converting === it.deal_id ? <Loader2 className="w-3 h-3 animate-spin" /> : converted[it.deal_id] ? <Check className="w-3 h-3" /> : <UserPlus className="w-3 h-3" />}
                    {converted[it.deal_id] ? "Lead créé" : "→ Lead"}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
